/** Formatting shared by the admin and tenant views. */

/**
 * A number as it was typed on a Slovak keyboard.
 *
 * `Number('0,5')` is NaN, which is how a comma-typed heating coefficient
 * used to be saved as a zero. The backend accepts both separators; this is
 * for the places where the form reads its own fields before sending them.
 * A dot counts as grouping only next to a comma — otherwise `6.60` would
 * turn into 660.
 */
export function typedNumber(value: string | null | undefined) {
  if (value === null || value === undefined) return NaN
  const text = value.trim().replace(/[\s\u00a0]/g, '')
  return Number(text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text)
}

export function money(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return '—'
  const number = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(number)) return '—'
  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR'
  }).format(number)
}

/**
 * What a block's readings and consumption are counted in, in the same
 * vocabulary the engine uses on result lines.
 */
export const BLOCK_UNITS: Record<string, string> = {
  HOT_WATER: 'm3',
  COLD_WATER: 'm3',
  ELECTRICITY: 'kWh'
}

/** The unit as it is written for a reader: `m3` is really m³. */
export function unitLabel(unit?: string | null) {
  if (!unit) return null
  return unit === 'm3' ? 'm³' : unit === 'month' ? 'mes.' : unit
}

export function quantity(value: string | null | undefined, unit?: string | null) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (Number.isNaN(number)) return null
  const formatted = new Intl.NumberFormat('sk-SK', {
    // Readings are stored at whatever precision the meter shows.
    maximumFractionDigits: 6
  }).format(number)
  const label = unitLabel(unit)
  return label ? `${formatted} ${label}` : formatted
}

export function unitPrice(value: string | null | undefined) {
  if (!value) return null
  const number = Number(value)
  if (Number.isNaN(number)) return null
  return new Intl.NumberFormat('sk-SK', {
    // Mirrors the backend's display_price: two places at minimum, six at
    // most, trailing zeros dropped in between.
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  }).format(number)
}

export function percent(share: string | null | undefined) {
  if (!share) return null
  const number = Number(share)
  if (Number.isNaN(number)) return null
  return `${new Intl.NumberFormat('sk-SK', {
    maximumFractionDigits: 2
  }).format(number * 100)} %`
}

export function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  const [year, month, day] = value.slice(0, 10).split('-')
  return `${Number(day)}. ${Number(month)}. ${year}`
}

/** True when the amount is something the tenant still owes. */
export function isUnderpayment(balance: string | null | undefined) {
  return Number(balance ?? 0) >= 0
}

export function absoluteAmount(balance: string | null | undefined) {
  return Math.abs(Number(balance ?? 0))
}

/** A share of the year expressed back as months, for annual-amount lines. */
export function months(share: string | null | undefined) {
  if (!share) return null
  const number = Number(share)
  if (Number.isNaN(number)) return null
  return new Intl.NumberFormat('sk-SK', {
    maximumFractionDigits: 1
  }).format(number * 12)
}

/**
 * The period expressed in months, mirroring the backend's `month_units`.
 * Computed here as well so the wizard can show it while the dates are being
 * typed, instead of only after a round trip.
 */
function parseISODate(value: string) {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return { year: year!, month: month!, day: day! }
}

function utc(year: number, month: number, day: number) {
  return Date.UTC(year, month - 1, day)
}

function daysInMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

const DAY_MS = 86_400_000

function inclusiveDays(startMs: number, endMs: number) {
  return Math.round((endMs - startMs) / DAY_MS) + 1
}

/** Rounds to the nearest step, matching Python's ROUND_HALF_UP. */
function roundTo(value: number, step: number) {
  return Math.round(value / step + Number.EPSILON) * step
}

export function periodMonths(
  start: string | undefined,
  end: string | undefined,
  base: string,
  referenceStart?: string,
  referenceEnd?: string
): number | null {
  if (!start || !end) return null

  const from = parseISODate(start)
  const to = parseISODate(end)
  const startMs = utc(from.year, from.month, from.day)
  const endMs = utc(to.year, to.month, to.day)
  if (endMs < startMs) return null

  if (base === 'DAYS') {
    if (!referenceStart || !referenceEnd) return null
    const ref = {
      from: parseISODate(referenceStart),
      to: parseISODate(referenceEnd)
    }
    const refDays = inclusiveDays(
      utc(ref.from.year, ref.from.month, ref.from.day),
      utc(ref.to.year, ref.to.month, ref.to.day)
    )
    if (refDays <= 0) return null
    return (inclusiveDays(startMs, endMs) / refDays) * 12
  }

  const step = base === 'HALF_MONTHS' ? 0.5 : 1
  let total = 0
  let year = from.year
  let month = from.month

  while (year * 12 + month <= to.year * 12 + to.month) {
    const length = daysInMonth(year, month)
    const coveredStart = Math.max(utc(year, month, 1), startMs)
    const coveredEnd = Math.min(utc(year, month, length), endMs)
    total += roundTo(inclusiveDays(coveredStart, coveredEnd) / length, step)

    month += 1
    if (month > 12) {
      year += 1
      month = 1
    }
  }

  return total
}

export function formatMonths(value: number) {
  return new Intl.NumberFormat('sk-SK', {
    maximumFractionDigits: 1
  }).format(value)
}
